import React, { useState } from "react";
import ProjectCard from "../components/ProjectCard";
import { ENDPOINTS } from "../api/api";
import useFetch from "../hooks/useFetch";
import "./Portfolio.css";
import { useTranslation } from "react-i18next";
import "../i18n";

function Portfolio() {
  const { t } = useTranslation();
  const [filter, setFilter] = useState("All");
  const [search, setSearch] = useState("");

  const { data: categoriesData, loading: catLoading } = useFetch(ENDPOINTS.portfolioCategories);
  const { data: projectsData, loading: projLoading } = useFetch(ENDPOINTS.projects);

  const categories = categoriesData || [];
  const projects   = projectsData   || [];
  const loading    = catLoading || projLoading;

  const filteredProjects = projects.filter((p) => {
    const matchCategory = filter === "All" || p.category === filter;
    const matchSearch   = p.title.toLowerCase().includes(search.toLowerCase());
    return matchCategory && matchSearch;
  });

  const counts = categories.reduce((acc, cat) => {
    acc[cat.name] =
      cat.name === "All"
        ? projects.length
        : projects.filter((p) => p.category === cat.name).length;
    return acc;
  }, {});

  return (
    <div className="pf-page">

      {/* ── Hero ── */}
      <section className="pf-hero">
        <div className="pf-hero-bg">
          <div className="pf-orb pf-orb--1"></div>
          <div className="pf-orb pf-orb--2"></div>
          <div className="pf-hero-grid"></div>
        </div>
        <div className="pf-hero-content">
          <span className="pf-eyebrow">
            <i className="fa-solid fa-briefcase"></i>
            {t("portfolio.eyebrow")}
          </span>
          <h1 className="pf-hero-title">
            {t("portfolio.title")}
            <span className="pf-hero-highlight"> {t("portfolio.highlight")}</span>
          </h1>
          <p className="pf-hero-subtitle">{t("portfolio.subtitle")}</p>

          <div className="pf-hero-stats">
            <div className="pf-hero-stat">
              <span className="pf-hero-stat-value">{projects.length}+</span>
              <span className="pf-hero-stat-label">{t("portfolio.projects")}</span>
            </div>
            <div className="pf-hero-stat-divider"></div>
            <div className="pf-hero-stat">
              <span className="pf-hero-stat-value">
                {categories.length > 0 ? categories.length - 1 : 0}
              </span>
              <span className="pf-hero-stat-label">{t("portfolio.categories")}</span>
            </div>
            <div className="pf-hero-stat-divider"></div>
            <div className="pf-hero-stat">
              <span className="pf-hero-stat-value">100%</span>
              <span className="pf-hero-stat-label">{t("portfolio.delivered")}</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Controls ── */}
      <section className="pf-controls">
        <div className="pf-container">
          <div className="pf-search-wrap">
            <i className="fa-solid fa-magnifying-glass pf-search-icon"></i>
            <input
              type="text"
              className="pf-search"
              placeholder={t("portfolio.search")}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
            {search && (
              <button className="pf-search-clear" onClick={() => setSearch("")}>
                <i className="fa-solid fa-xmark"></i>
              </button>
            )}
          </div>

          <div className="pf-filters">
            {categories.map((cat) => (
              <button
                key={cat.id}
                className={`pf-filter-btn ${filter === cat.name ? "pf-filter-btn--active" : ""}`}
                onClick={() => setFilter(cat.name)}
              >
                <i className={cat.icon}></i>
                {cat.name}
                <span className="pf-filter-count">{counts[cat.name] ?? 0}</span>
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* ── Projects Grid ── */}
      <section className="pf-grid-section">
        <div className="pf-container">

          <div className="pf-results-bar">
            <span className="pf-results-count">
              {filteredProjects.length}{" "}
              {filteredProjects.length === 1 ? t("portfolio.found") : t("portfolio.foundPlural")}
              {filter !== "All" && <> {t("portfolio.in")} <strong>{filter}</strong></>}
              {search && <> {t("portfolio.matching")} <strong>"{search}"</strong></>}
            </span>
            {(filter !== "All" || search) && (
              <button className="pf-clear-btn" onClick={() => { setFilter("All"); setSearch(""); }}>
                <i className="fa-solid fa-xmark"></i>
                {t("portfolio.clearFilters")}
              </button>
            )}
          </div>

          {loading && <div className="pf-loading"><div className="pf-spinner"></div></div>}

          {!loading && filteredProjects.length === 0 && (
            <div className="pf-empty">
              <i className="fa-solid fa-folder-open pf-empty-icon"></i>
              <h3 className="pf-empty-title">{t("portfolio.noProjects")}</h3>
              <p className="pf-empty-desc">{t("portfolio.noProjectsDesc")}</p>
              <button className="pf-empty-btn" onClick={() => { setFilter("All"); setSearch(""); }}>
                {t("portfolio.clearBtn")}
              </button>
            </div>
          )}

          {!loading && filteredProjects.length > 0 && (
            <div className="pf-grid">
              {filteredProjects.map((project, i) => (
                <ProjectCard key={project.id} project={project} index={i} />
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="pf-cta-section">
        <div className="pf-cta-inner">
          <div className="pf-cta-orb"></div>
          <span className="pf-section-eyebrow">
            <i className="fa-solid fa-rocket"></i>
            {t("portfolio.ctaEyebrow")}
          </span>
          <h2 className="pf-cta-title">{t("portfolio.ctaTitle")}</h2>
          <p className="pf-cta-sub">{t("portfolio.ctaSub")}</p>
          <div className="pf-cta-actions">
            <a href="/contact" className="pf-btn-primary">
              <i className="fa-solid fa-paper-plane"></i>
              {t("portfolio.startProject")}
            </a>
            <a href="/services" className="pf-btn-secondary">
              {t("portfolio.ourServices")}
              <i className="fa-solid fa-arrow-right"></i>
            </a>
          </div>
        </div>
      </section>

    </div>
  );
}

export default Portfolio;